import React, { useState } from 'react'
import { YStack, Text, ScrollView, Separator, Label, RadioGroup, XStack, getVariable } from '@red-ui/components'
import { DayPicker, DateRange as DayPickerDateRange } from 'react-day-picker'
import { getString } from '@vrw/data'
import { formatDateForDisplay } from '../../helpers/dateHelpers'
import { DateRange } from './useSearchForm'
import { isBefore } from 'date-fns'

import 'react-day-picker/style.css'

export function CalendarPicker({ value, onChange }: { readonly value: DateRange; readonly onChange: (date: DateRange) => void }) {
  const [isRoundTrip, setIsRoundTrip] = useState(!!value.to)
  const [internalValue, setInternalValue] = useState<DateRange>(value)

  const handleChange = (range: DayPickerDateRange) => {
    const { from: newFrom, to: newTo } = range
    const { from: currentFrom, to: currentTo } = internalValue
    const isRangeSelected = currentFrom && currentTo

    if (isRangeSelected && newFrom) {
      const resetFrom = isBefore(newFrom, currentFrom) ? newFrom : newTo
      setInternalValue({ from: resetFrom } as DateRange)
      return
    }

    if (!currentTo && newFrom) {
      if (isBefore(newFrom, currentFrom)) {
        setInternalValue({ from: newFrom })
      } else {
        onChange({ from: newFrom, to: newTo ?? newFrom })
      }
    }
  }

  return (
    <YStack flex={1} gap="$3">
      <XStack>
        <YStack flex={1} flexBasis="50%" backgroundColor="$red6" padding="$2" alignItems="center">
          <Text fontWeight="600" color="$white" fontSize="$3">
            {getString('redSkySearch.question.date.departure.label')}
          </Text>
          <Text color="$white">{formatDateForDisplay(internalValue.from)}</Text>
        </YStack>
        {isRoundTrip ? (
          <YStack flex={1} flexBasis="50%" backgroundColor="$pink10" padding="$2" alignItems="center">
            <Text fontWeight="600" fontSize="$3">
              {getString('redSkySearch.question.date.return.label')}
            </Text>
            <Text>
              {internalValue.to ? formatDateForDisplay(internalValue.to) : getString('redSkySearch.question.date.choosedate.label')}
            </Text>
          </YStack>
        ) : null}
      </XStack>
      <RadioGroup
        paddingHorizontal="$3"
        gap="$3"
        flexDirection="row"
        value={isRoundTrip.toString()}
        onValueChange={(value) => setIsRoundTrip(value === 'true')}>
        <XStack alignItems="center" gap="$3">
          <Label htmlFor="round-trip">{getString('redSkySearch.question.date.roundtrip.label')}</Label>
          <RadioGroup.Item value="true" size="$5" id="round-trip">
            <RadioGroup.Indicator backgroundColor="$red6" />
          </RadioGroup.Item>
        </XStack>
        <XStack alignItems="center" gap="$3">
          <Label htmlFor="one-way">{getString('redSkySearch.question.date.onewaytrip.label')}</Label>
          <RadioGroup.Item value="false" size="$5" id="one-way">
            <RadioGroup.Indicator backgroundColor="$red6" />
          </RadioGroup.Item>
        </XStack>
      </RadioGroup>
      <Separator />
      <ScrollView
        contentContainerStyle={{
          paddingBottom: '$3',
          paddingHorizontal: '$3',
        }}>
        {/*
         * Not ideal to do this here, but only way to load dynamic variables (name seem to keep changing)
         * https://react-day-picker.js.org/docs/styling -> does not seem to work with inline styling
         */}
        <style>
          {`
            .red-sky-calendar-picker {
              /* uses values from red-ui lib */
              --rdp-accent-color: ${getVariable('$red6', 'color')};
              --rdp-range_middle-background-color: ${getVariable('$pink6', 'color')};
            }
          `}
        </style>
        <DayPicker
          animate
          timeZone="UTC"
          autoFocus
          required
          ISOWeek
          hideNavigation
          className="red-sky-calendar-picker"
          // NOTE: For now we limit to the current year
          disabled={{ before: new Date(), after: new Date(`${new Date().getFullYear()}-12-31`) }}
          numberOfMonths={12}
          {...(isRoundTrip
            ? { mode: 'range', selected: internalValue, onSelect: handleChange, min: 0 }
            : { mode: 'single', selected: internalValue.from, onSelect: (date) => date && onChange({ from: date, to: undefined }) })}
          styles={{
            months: {
              justifyContent: 'center',
            },
            month: {
              width: '100%',
            },
            month_grid: {
              width: '100%',
            },
            day_button: {
              margin: 'auto',
            },
            month_caption: {
              fontWeight: 600,
            },
          }}
        />
      </ScrollView>
    </YStack>
  )
}
